import React, { useEffect, useState } from "react";
import axios from "axios";
import { BookHeart, Calendar, Crown, Download, Lock, Sparkles } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// "2026-07-07T04:00:00+00:00" → "Tue, Jul 7"
const formatDropDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
};

// Human-friendly countdown: "3d · 14h", "22h · 15m", "45m", or "" if past/invalid.
const formatCountdown = (iso, now) => {
    if (!iso) return "";
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return "";
    const diff = target - now;
    if (diff <= 0) return "";
    const totalMinutes = Math.floor(diff / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d · ${hours}h`;
    if (hours > 0) return `${hours}h · ${minutes}m`;
    return `${minutes}m`;
};

/**
 * Public "Free Resources" hub — lives between Episodes and Music on the homepage.
 * Currently surfaces two cards: the Episode 2 worksheet (open download), and the
 * founding-100 Prayer Journal (locked behind the waitlist signup).
 */
export const Resources = () => {
    // Fetch episode release schedule so worksheets stay gated until their episode drops.
    const [episodesByFreebie, setEpisodesByFreebie] = useState({});
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);
    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API_BASE}/api/episodes`)
            .then((r) => {
                if (!mounted) return;
                const map = {};
                for (const ep of r.data || []) {
                    // The backend hides freebie_url when unreleased, so key by number instead.
                    map[ep.number] = ep;
                }
                setEpisodesByFreebie(map);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);
    const items = [
        {
            id: "btc-worksheet",
            episodeNumber: 2,
            badge: "Episode 02 · Companion",
            title: "Breaking the Cycle",
            subtitle: "Worksheet",
            description:
                "A 3-page printable companion to Episode Two. Walk through the five-step framework — name the pattern, honor the survival, find the lie, replace it with God's truth, and choose the one opposite act.",
            type: "open",
            cta: "Download free",
            href: `${API_BASE}/api/freebies/breaking-the-cycle.pdf`,
            icon: Download,
        },
        {
            id: "people-pleasing-worksheet",
            episodeNumber: 3,
            badge: "Episode 03 · Companion",
            title: "Your No",
            subtitle: "is Holy",
            description:
                "A 3-page companion to Episode Three. Name where people-pleasing shows up, find your root, practice the 24-hour ask, set one boundary, and pray a release in your own words.",
            type: "open",
            cta: "Download free",
            href: `${API_BASE}/api/freebies/overcoming-people-pleasing.pdf`,
            icon: Download,
        },
        {
            id: "identity-wounds-worksheet",
            episodeNumber: 4,
            badge: "Episode 04 · Companion",
            title: "Give the Name",
            subtitle: "Back",
            description:
                "A 3-page companion to Episode Four. Name the lie you've been answering to, trace the doorway it came through, receive the opposite truth from the Word, and reclaim your real name.",
            type: "open",
            cta: "Download free",
            href: `${API_BASE}/api/freebies/healing-identity-wounds.pdf`,
            icon: Download,
        },
        {
            id: "self-worth-worksheet",
            episodeNumber: 5,
            badge: "Episode 05 · Companion",
            title: "Remember",
            subtitle: "Your Worth",
            description:
                "A 3-page companion to Episode Five. Identify your worth-wound, name where you've been earning what was already yours, write your daily declaration, and take one act of unearned rest.",
            type: "open",
            cta: "Download free",
            href: `${API_BASE}/api/freebies/remember-your-worth.pdf`,
            icon: Download,
        },
        {
            id: "finale-worksheet",
            episodeNumber: 6,
            badge: "Episode 06 · Finale",
            title: "Stop Shrinking,",
            subtitle: "Start Rising",
            description:
                "A 4-page Season 1 finale companion. Run the Stop Shrinking Audit, speak your Rising Declaration, map a 90-day vision in voice / room / dream, and seal it with a commissioning prayer anchored in Jeremiah 29:11.",
            type: "finale",
            cta: "Download free",
            href: `${API_BASE}/api/freebies/stop-shrinking-start-rising.pdf`,
            icon: Download,
        },
        {
            id: "prayer-journal",
            badge: "Founding 100 · Exclusive",
            title: "The Becoming",
            subtitle: "Prayer Journal",
            description:
                "Six soft days of scripture, reflection, and soul-truth — for the woman breaking the cycle and returning home to herself. Sent instantly to the first 100 women on the waitlist.",
            type: "locked",
            cta: "Claim your seat",
            href: "#join",
            icon: Lock,
        },
    ];

    return (
        <section
            id="resources"
            data-testid="resources-section"
            className="relative py-24 md:py-32"
        >
            <div className="mx-auto max-w-6xl px-6 md:px-10">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="eyebrow text-gold-deep mb-4 flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5" /> Free for the becoming
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-lilac-900 leading-tight">
                            Resources to walk{" "}
                            <em className="italic gold-text">with you</em>.
                        </h2>
                        <p className="mt-5 text-lilac-700 max-w-xl leading-relaxed">
                            Branded printables, prayer journals, and episode companions — built to help
                            you do the work between episodes. Free, always.
                        </p>
                    </div>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((item) => {
                        const Icon = item.icon;
                        const isLocked = item.type === "locked";
                        const isFinale = item.type === "finale";
                        const ep = item.episodeNumber != null ? episodesByFreebie[item.episodeNumber] : null;
                        const isScheduled = ep && ep.is_released === false && !!ep.release_at;
                        const dropLabel = isScheduled ? `Drops ${formatDropDate(ep.release_at)}` : "";
                        const countdown = isScheduled ? formatCountdown(ep.release_at, now) : "";
                        return (
                            <article
                                key={item.id}
                                data-testid={`resource-${item.id}`}
                                className={`group relative overflow-hidden rounded-[32px] p-7 md:p-9 border transition-all hover:-translate-y-1 hover:shadow-[0_30px_80px_rgba(43,29,53,0.10)] ${
                                    isLocked
                                        ? "bg-gradient-to-br from-lilac-100 via-white to-[#FAF5FC] border-lilac-200"
                                        : isFinale
                                        ? "bg-gradient-to-br from-[#FFEFC4] via-white to-[#F3EDF5] border-gold ring-1 ring-gold/40"
                                        : "bg-gradient-to-br from-[#FFF7E5] via-white to-[#FAF5FC] border-gold/30"
                                } ${isScheduled ? "opacity-95" : ""}`}
                            >
                                {isFinale && (
                                    <div
                                        data-testid={`resource-finale-ribbon-${item.id}`}
                                        className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold text-white text-[10px] font-bold tracking-[0.18em] uppercase shadow-[0_8px_22px_rgba(212,175,55,0.45)]"
                                    >
                                        <Crown className="w-3 h-3" />
                                        S1 Finale
                                    </div>
                                )}

                                <div
                                    className={`absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl ${
                                        isLocked
                                            ? "bg-lilac-300/40"
                                            : isFinale
                                            ? "bg-gold/40"
                                            : "bg-gold/25"
                                    }`}
                                    aria-hidden
                                />

                                <div className="relative">
                                    <div
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.16em] uppercase font-bold ${
                                            isLocked
                                                ? "bg-lilac-200 text-lilac-900 border border-lilac-200"
                                                : "bg-gold/15 text-gold-deep border border-gold/40"
                                        }`}
                                    >
                                        {isLocked ? (
                                            <Lock className="w-3 h-3" />
                                        ) : isFinale ? (
                                            <Sparkles className="w-3 h-3" />
                                        ) : (
                                            <BookHeart className="w-3 h-3" />
                                        )}
                                        {item.badge}
                                    </div>

                                    <h3 className="mt-6 font-display text-3xl md:text-4xl text-lilac-900 leading-tight">
                                        {item.title}
                                        <br />
                                        <em className="italic gold-text">{item.subtitle}.</em>
                                    </h3>

                                    <p className="mt-5 text-sm md:text-base text-lilac-700 leading-relaxed max-w-md">
                                        {item.description}
                                    </p>

                                    {isScheduled ? (
                                        <div
                                            data-testid={`resource-drops-${item.id}`}
                                            className="mt-8 inline-flex flex-col items-start gap-1"
                                            aria-label={`This worksheet unlocks when Episode ${item.episodeNumber} drops`}
                                        >
                                            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-lilac-100 text-lilac-900 font-semibold text-sm border border-lilac-200 cursor-default">
                                                <Calendar className="w-4 h-4 text-gold-deep" />
                                                {dropLabel}
                                            </div>
                                            {countdown && (
                                                <span
                                                    data-testid={`resource-countdown-${item.id}`}
                                                    className="pl-2 text-[11px] italic text-gold-deep tracking-wide"
                                                >
                                                    Drops in {countdown}
                                                </span>
                                            )}
                                        </div>
                                    ) : isLocked ? (
                                        <a
                                            href={item.href}
                                            data-testid={`resource-cta-${item.id}`}
                                            className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-lilac-900 hover:bg-lilac-700 text-white font-semibold text-sm shadow-[0_12px_32px_rgba(43,29,53,0.18)] transition-all"
                                        >
                                            <Icon className="w-4 h-4" />
                                            {item.cta}
                                        </a>
                                    ) : (
                                        <a
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`resource-cta-${item.id}`}
                                            className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-gold hover:bg-gold-deep text-white font-semibold text-sm shadow-[0_12px_32px_rgba(212,175,55,0.35)] transition-all"
                                        >
                                            <Icon className="w-4 h-4" />
                                            {item.cta}
                                        </a>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>

                {/* Tease for more */}
                <p
                    data-testid="resources-tease"
                    className="mt-10 text-center text-sm italic text-lilac-700/80"
                >
                    More worksheets, prayer guides, and printables drop with every new episode. 💜
                </p>
            </div>
        </section>
    );
};
